import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  Button,
  Typography,
  IconButton,
  Fade,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import LabelIcon from '@material-ui/icons/Label';
import { useStyles } from './ManageTagsModalStyles';

interface ManageTagsModalProps {
  open: boolean;
  onClose: () => void;
  tags: string[];
  onSave: (tags: string[]) => void;
}

export default function ManageTagsModal({
  open,
  onClose,
  tags,
  onSave,
}: ManageTagsModalProps) {
  const classes = useStyles();
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open && !isInitialized) {
      setLocalTags(Array.isArray(tags) ? [...tags] : []);
      setIsInitialized(true);
      setErrorMessage('');
    } else if (!open) {
      setIsInitialized(false);
      setErrorMessage('');
    }
  }, [open, tags, isInitialized]);

  const handleNameChange = (index: number, value: string) => {
    const updated = [...localTags];
    updated[index] = value;
    setLocalTags(updated);
    if (errorMessage) setErrorMessage('');
  };

  const handleDelete = (index: number) => {
    setLocalTags(localTags.filter((_, i) => i !== index));
  };

  const handleAddNew = () => {
    setLocalTags([...localTags, '']);
  };

  const handleSave = () => {
    const trimmed = localTags.map(t => t.trim());
    if (trimmed.some(t => !t)) {
      setErrorMessage('Preencha o nome de todas as tags.');
      return;
    }
    const unique = Array.from(new Set(trimmed));
    if (unique.length !== trimmed.length) {
      setErrorMessage('Existem tags duplicadas.');
      return;
    }
    onSave(unique);
    onClose();
  };

  const handleCancel = () => {
    setLocalTags([...tags]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      className={classes.modal}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <div>
              <Typography className={classes.title}>
                Gerenciar Tags
              </Typography>
              <Typography className={classes.subtitle}>
                {localTags.length} tag{localTags.length !== 1 ? 's' : ''} cadastrada{localTags.length !== 1 ? 's' : ''}
              </Typography>
            </div>
            <IconButton className={classes.closeButton} onClick={handleCancel} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.tagList}>
            {localTags.length === 0 ? (
              <div className={classes.emptyState}>
                <LabelIcon className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  Nenhuma tag cadastrada.<br />
                  Adicione uma nova tag para comecar.
                </Typography>
              </div>
            ) : (
              localTags.map((tag, index) => (
                <div key={index} className={classes.tagRow}>
                  <span className={classes.rowIndex}>{index + 1}</span>
                  <TextField
                    className={classes.input}
                    label="Nome"
                    value={tag}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Ex: Scalping"
                  />
                  <IconButton
                    className={classes.deleteButton}
                    onClick={() => handleDelete(index)}
                    size="small"
                    title="Remover tag"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))
            )}
          </div>

          {errorMessage && (
            <Typography className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          )}

          <Button
            className={classes.addButton}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Adicionar nova tag
          </Button>

          <div className={classes.actionButtons}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              className={classes.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              className={classes.saveButton}
            >
              Salvar
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
